import { RulesList } from '../rules/rules-list';
import { TuningsConfig } from '../config/config';
import { getAllElements } from '../../utils/get-all-elements/get-all-elements';
import type { PerformanceService } from '../performance-service/performance-service';
import { useDeferredRunner } from '../performance-control/use-deferred-runner';
import { PostMessageType } from '../../constants/base-api';
import { onPostMessage } from '../api/on-post-message';

interface RemediationManager {
  run: (elements: HTMLElement[]) => void;
  init: () => void;
  onDomUpdates: (elements: HTMLElement[]) => void;
}

const ENV: string = '__ENV__';

export const createRemediationManager = (): RemediationManager => {
  let remediationInProgress: boolean = false;
  let performanceService: PerformanceService;
  let remediationElementsQueue: HTMLElement[] = [];

  const runCallback = (elements: HTMLElement[]): void => {
    const measureId = performanceService?.startMeasurement();
    // TODO: check do we need run it cause now remediation is sync
    if (remediationInProgress) {
      return;
    }

    remediationInProgress = true;
    RulesList.forEach((rule) => {
      rule.run(elements);
    });
    remediationInProgress = false;
    remediationElementsQueue = [];

    performanceService?.stopMeasurement(measureId);
  };

  const { run } = useDeferredRunner(runCallback);

  const init = async (): Promise<void> => {
    if (ENV === 'development') {
      const performanceServiceModule = await import(
        '../performance-service/performance-service'
      );

      performanceService = performanceServiceModule.performanceService;
    }

    // check if run remediation on tab enabled
    if (!TuningsConfig.tech_rem_on_tab) {
      const allElements = getAllElements();
      run([...allElements]);
    }

    const unsubscribe = onPostMessage(
      PostMessageType.KeyboardNavEnabled,
      () => {
        const allElements = getAllElements();
        run([...allElements]);
        unsubscribe();
      },
    );
  };

  const onDomUpdates = (elements: HTMLElement[]): void => {
    /** PUSHING MUTATED ELEMENTS TO ARRAY */
    remediationElementsQueue.push(...elements);
    /** THROTTLED/DEBOUNCED CALL */
    run(remediationElementsQueue);
  };

  return { run, init, onDomUpdates };
};

export const remediationManager = createRemediationManager();
